import React, { useEffect, useState } from 'react'
import { Alert, Typography } from '@mui/material'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
} from '../../styles/StyledComponents'
import { transferOwnership } from '../../../../services/rbacService'
import { useRbacStore } from '../../../../stores/rbacStore'
import { addNotification } from '../../../../stores/NotificationStore'
import store from '../../../../stores'
import { describeAccessError } from '../../../../utils/rbacMessages'
import type { ManageAccessMember } from './ManageAccessDrawer'

interface TransferOwnershipDialogProps {
  open: boolean
  companyId: string
  /** The person who becomes the Owner. */
  member: ManageAccessMember | null
  onClose: () => void
  onTransferred: () => void
}

/**
 * Owner only (SC-439). The new Owner gets every permission and the person confirming becomes an
 * Admin. Other Owners are untouched: a company may have several, and only a platform admin
 * demotes or replaces them.
 */
export const TransferOwnershipDialog: React.FC<TransferOwnershipDialogProps> = ({
  open,
  companyId,
  member,
  onClose,
  onTransferred,
}) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setBusy(false)
      setError(null)
    }
  }, [open, member?.userId])

  if (!member) return null

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await transferOwnership(companyId, member.userId)
      store.dispatch(
        addNotification({
          message: `${member.name} is now an Owner. You are now an Admin.`,
          type: 'success',
          duration: 6000,
        })
      )
      onTransferred()
      onClose()
    } catch (failure) {
      setError(describeAccessError(failure, useRbacStore.getState().catalogue))
    } finally {
      setBusy(false)
    }
  }

  return (
    <StyledDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>Transfer ownership</StyledDialogTitle>
      <StyledDialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {member.name} gets every permission, including deleting the workspace.
        </Alert>
        <Typography
          variant="body1"
          sx={{ color: 'text.primary', mb: 1 }}
          data-testid="transfer-ownership-text"
        >
          Make {member.name} an Owner of this workspace? You become an Admin.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Other Owners of this workspace keep their ownership. The change is written to the audit
          log.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="transfer-ownership-error">
            {error}
          </Alert>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined" disabled={busy}>
          Cancel
        </StyledButton>
        <StyledButton
          onClick={confirm}
          variant="contained"
          color="error"
          disabled={busy}
          data-testid="transfer-ownership-confirm"
        >
          {busy ? 'Transferring...' : 'Transfer ownership'}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}

export default TransferOwnershipDialog
